<template>
  <BaseWidget
    title="Tour → member conversions rate"
    :show-info-icon="true"
    info-tooltip="Percentage of tour visitors who converted to revenue generating users within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/4088442-how-do-i-enable-leads-to-book-tours-from-my-website', text: 'Learn about Tours' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <MetricValue
        :value="conversionRateFormatted"
        :subtitle="dateRangeLabel"
      >
        <template #comparison>
          <ComparisonChip
            :change="changePercentage"
            :previous-value="previousRateFormatted"
            :previous-period-dates="previousTimestamps"
            :date-range-preset="dateRange"
            label="Tour conversion rate"
          />
        </template>
      </MetricValue>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useDashboardKPIsData } from '../composables/useWidgetData.js'
import { DATE_RANGE_LABELS, getPreviousPeriodTimestamps } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'

const props = defineProps({
  rowSpan: { type: Number, default: 1 },
  colSpan: { type: Number, default: 1 },
  editMode: { type: Boolean, default: false },
  props: { type: Object, default: () => ({}) },
  widgetId: { type: String, default: null },
  sectionId: { type: String, default: null },
  widgetType: { type: String, default: null },
  priority: { type: Number, default: null },
  focused: { type: Boolean, default: false }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const dateRange = ref(props.props?.dateRange || 'last_30_days')

const dateRangeLabel = computed(() => DATE_RANGE_LABELS[dateRange.value] || 'Last 30 days')

const { data, loading, error, cleanup } = useDashboardKPIsData({
  variables: computed(() => ({ dateRange: dateRange.value })),
  priority: props.priority ?? 1
})

const previousTimestamps = computed(() => getPreviousPeriodTimestamps(dateRange.value))

const { data: previousData, loading: previousLoading, cleanup: previousCleanup } = useDashboardKPIsData({
  variables: computed(() => {
    const ts = previousTimestamps.value
    return { from_timestamp: ts.from_timestamp, to_timestamp: ts.to_timestamp }
  }),
  priority: props.priority ?? 1,
  totalsOnly: true
})

function findKpiByName(kpis, ...searchTerms) {
  if (!Array.isArray(kpis)) return null
  const lower = (s) => String(s || '').toLowerCase()
  for (const kpi of kpis) {
    const name = lower(kpi.name)
    if (!name) continue
    const matchesAll = searchTerms.every((term) => name.includes(lower(term)))
    if (matchesAll) return kpi
  }
  return null
}

function extractTourRate(kpis) {
  return findKpiByName(kpis, 'tour', 'conversion') || findKpiByName(kpis, 'tour', 'rate') ||
    findKpiByName(kpis, 'tours', 'conversion') || findKpiByName(kpis, 'tours', 'rate')
}

const conversionRateRaw = computed(() => {
  const kpis = data.value?.dashboardKPIs?.kpis ?? []
  const kpi = extractTourRate(kpis)
  if (!kpi && kpis.length > 0) {
  }
  const value = kpi?.value
  if (value === null || value === undefined || value === '') return null
  const num = parseFloat(value)
  return Number.isNaN(num) ? null : num
})

const conversionRateFormatted = computed(() => {
  if (conversionRateRaw.value === null) return '—'
  return `${conversionRateRaw.value.toFixed(1)}%`
})

const previousRateRaw = computed(() => {
  const kpis = previousData.value?.dashboardKPIs?.kpis ?? []
  const kpi = extractTourRate(kpis)
  const value = kpi?.value
  if (value === null || value === undefined || value === '') return null
  const num = parseFloat(value)
  return Number.isNaN(num) ? null : num
})

const previousRateFormatted = computed(() => {
  if (previousRateRaw.value === null) return null
  return `${previousRateRaw.value.toFixed(1)}%`
})

const changePercentage = computed(() => {
  if (loading.value || previousLoading.value) return null
  const current = conversionRateRaw.value
  const previous = previousRateRaw.value
  if (current === null || previous === null) return null
  if (previous === 0 && current === 0) return 0
  if (previous === 0) return 100
  return Math.round(((current - previous) / previous) * 1000) / 10
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

watch(() => props.props?.dateRange, (v) => { if (v && v !== dateRange.value) dateRange.value = v })

onUnmounted(() => { cleanup(); previousCleanup() })
</script>

<style scoped>
.widget-content-wrapper { display: flex; flex-direction: column; height: 100%; min-height: 0; gap: 12px; }
.loading-state, .error-state { padding: 20px; text-align: center; color: rgba(0, 0, 0, 0.87); font-size: 14px; }
.error-state { color: var(--color-red); }
.conversion-subtitle { font-size: 13px; color: rgba(0, 0, 0, 0.87); }
</style>
